import {
  selectLocalizedLanguages,
  updateLocalizedStringFromExcel,
} from "../../app/slices/TrainingLocalizationSlice";
import { useSelector } from "react-redux";
import { selectTrainingName } from "../../app/slices/TrainingSlice";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import { selectTrainingLocalizationState } from "../../app/slices/TrainingLocalizationSlice";
import { useDispatch } from "react-redux";
import { ChangeEvent, useRef } from "react";
import XLSX from "sheetjs-style";
import * as FileSaver from "file-saver";
import { v4 as uuidv4 } from "uuid";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";

// Excel column letter for a zero-based column index (0 -> A, 26 -> AA, ...).
function colLetter(index: number): string {
  var letters = "";
  var i = index;
  do {
    letters = String.fromCharCode(65 + (i % 26)) + letters;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return letters;
}

// Rich text message values contain markup tags (<b>, <i>, <u>, <s>, size and
// color spans — see src/features/rich_text/RichTextHtmlUtils.ts). They travel
// raw in the exported cells; translators must preserve the tags around the
// translated text, like any other markup.
export default function useTrainingLocalizationExcel() {
  const locState = useSelector(selectTrainingLocalizationState);
  const localizedLanguages = useSelector(selectLocalizedLanguages);
  const referenceLanguage = useSelector(selectReferenceLanguage);
  const projName = useSelector(selectTrainingName);
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const supportedFiletypes =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const downloadExcel = (shortcodes: string[]) => {
    const filetype =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const fileExtension = ".xlsx";

    const entries = locState.localizedEntries;
    var primaryLanguage = referenceLanguage;

    const languages = [
      primaryLanguage,
      ...shortcodes.filter((s) => s !== primaryLanguage),
    ];

    const json: Object[] = [];
    for (var key in entries) {
      // Skip entries that have no text in any of the exported languages.
      const hasText = languages.some(
        (lang) => String(entries[key][lang].value ?? "").trim() !== ""
      );
      if (!hasText) continue;

      const jsonObject = {
        key: key,
        [primaryLanguage]: entries[key][primaryLanguage].value,
      };

      for (var shortcode of shortcodes) {
        if (shortcode === primaryLanguage) continue;
        jsonObject[shortcode] = entries[key][shortcode].value;
      }

      json.push(jsonObject);
    }

    const exportToExcel = async () => {
      const ws = XLSX.utils.json_to_sheet(json);

      // Column order matches the insertion order json_to_sheet uses for the keys.
      const headers = ["key", ...languages];

      // Size each column to fit its longest value (plus a small padding).
      ws["!cols"] = headers.map((h) => ({
        wch:
          Math.max(
            h.length,
            ...json.map((row) => String((row as any)[h] ?? "").length)
          ) + 2,
      }));

      // Autofilter on the header row (dropdown arrows on each column).
      ws["!autofilter"] = {
        ref: `A1:${colLetter(headers.length - 1)}${json.length + 1}`,
      };

      // Thin white border around every cell, like the reference table.
      const whiteBorder = {
        top: { style: "thin", color: { rgb: "FFFFFFFF" } },
        bottom: { style: "thin", color: { rgb: "FFFFFFFF" } },
        left: { style: "thin", color: { rgb: "FFFFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFFFF" } },
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "FF156082" } },
        border: whiteBorder,
      };
      const bandLight = {
        font: { color: { rgb: "FF000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "FFC0E6F5" } },
        border: whiteBorder,
      };
      const bandDark = {
        font: { color: { rgb: "FF000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "FF83CCEB" } },
        border: whiteBorder,
      };

      for (var row = 1; row <= json.length + 1; row++) {
        for (var col = 0; col < headers.length; col++) {
          const addr = colLetter(col) + row;
          // Ensure the cell exists so empty translation cells also get the banding.
          ws[addr] = ws[addr] ?? { t: "s", v: "" };
          ws[addr].s =
            row === 1 ? headerStyle : row % 2 === 0 ? bandLight : bandDark;
        }
      }

      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: filetype });
      FileSaver.saveAs(
        data,
        projName + "_" + shortcodes.join("_") + fileExtension
      );
    };

    exportToExcel();
  };

  const handleExcelInput = function (e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files?.length === 1) {
      //handleFiles(e.target.files);
      handleExcel(e.target.files);
    }
  };

  const handleExcel = function (files: FileList) {
    var file = files[0];
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e) => {
      if (e.target) {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetname = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetname];

        // Convert the sheet to an array rows
        const sheetData: any[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
        });

        // Get the first row (the headers)
        const headers: any[] = sheetData[0];

        // check first row for shortcodes
        const shortcodes = headers.slice(1);
        const transaction = { id: `excel-import-${uuidv4()}` };
        dispatch(beginHistoryTransaction(transaction));
        try {
          for (var shortcode of shortcodes) {
            if (!localizedLanguages.some((loc) => loc.shortcode === shortcode)) {
              continue;
            }
            const columnIndex: number = headers.findIndex((header) => {
              return header === shortcode;
            });
            const parsedData: any[][] = sheetData.map((row) => [
              row[0],
              row[columnIndex],
            ]);
            parseExcel(shortcode, parsedData);
          }
          dispatch(commitHistoryTransaction(transaction));
        } catch (error) {
          dispatch(cancelHistoryTransaction(transaction));
          throw error;
        }
      }
    };
  };

  const parseExcel = (shortcode: string, parsedData: any[][]) => {
    for (var i = 0; i < parsedData.length; ++i) {
      var record: any = parsedData[i];
      // update record in slice
      // key, language, value
      dispatch(
        updateLocalizedStringFromExcel({
          key: record[0],
          shortcode: shortcode,
          value: record[1],
        })
      );
    }
  };

  return {
    downloadExcel,
    handleExcelInput,
    inputRef,
    supportedFiletypes,
  };
}
